import { useEffect, useState } from 'react';
import { percentualParaTaxa, type ConferenciaCpmLinha, type ConferenciaCpmResponse, type SalvarConferenciaCpmDto, type SituacaoConferenciaCpm } from '@triade/contracts';
import { useConferenciaCpm, useSalvarConferenciaCpm } from '@/api/rateios';
import { ErroLista } from '@/components/cargos';
import { BotaoExportar, ErroExportacao, useExportar } from '@/components/exportar';
import { formatarCusto, formatarDataHora, formatarFracaoPercentual, decimalDigitado, decimalParaTextoCampo, fracaoParaPercentualTexto } from '@/lib/format';
import { mensagemApi } from './comum';

const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';

/** Rotulo e cor da situacao (a situacao vem pronta da API; a tela so escolhe o tom). */
const SITUACAO: Record<SituacaoConferenciaCpm, { rotulo: string; classe: string }> = {
  OK: { rotulo: 'Dentro da tolerância', classe: 'bg-success-bg text-success-ink' },
  FORA: { rotulo: 'Fora da tolerância', classe: 'bg-danger-bg text-danger-ink' },
  SEM_REFERENCIA: { rotulo: 'Sem referência', classe: 'bg-line-soft text-ink-secondary' },
};

/** Rascunho da linha: textos digitados. Comparado com o gravado por texto (nenhuma conta). */
interface Rascunho {
  referencia: string;
  explicacao: string;
}

function rascunhoDe(l: ConferenciaCpmLinha): Rascunho {
  return { referencia: decimalParaTextoCampo(l.referencia), explicacao: l.explicacao ?? '' };
}

function mudou(l: ConferenciaCpmLinha, r: Rascunho): boolean {
  return r.referencia.trim() !== decimalParaTextoCampo(l.referencia).trim() || r.explicacao.trim() !== (l.explicacao ?? '').trim();
}

/**
 * Aba "Conferencia" do rateio: CPM XARP x CPM de referencia (planilha do cliente) por centro. Diferenca, %, situacao e
 * cores vem da API. So com custo.ver + rateio.ver (a tela so monta a aba nesse caso); gravar exige rateio.editar.
 */
export function AbaConferencia({ competencia, podeEditar }: { competencia: string; podeEditar: boolean }) {
  const consulta = useConferenciaCpm(competencia);
  const salvar = useSalvarConferenciaCpm();
  const exportacao = useExportar();
  const [tolerancia, setTolerancia] = useState('');
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState(false);

  const dados: ConferenciaCpmResponse | undefined = consulta.data;

  /** Ao chegar dado novo (abertura ou apos gravar), o rascunho volta ao que a API devolveu. */
  useEffect(() => {
    if (!dados) return;
    setTolerancia(fracaoParaPercentualTexto(dados.tolerancia));
    setRascunhos(Object.fromEntries(dados.linhas.map((l) => [l.centroId, rascunhoDe(l)])));
  }, [dados]);

  if (consulta.isError && !dados) {
    return <ErroLista erro={consulta.error} onTentar={() => void consulta.refetch()} titulo="Não foi possível carregar a conferência do CPM" />;
  }
  if (!dados) return <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Carregando conferência…</p>;

  const toleranciaGravada = fracaoParaPercentualTexto(dados.tolerancia);
  const toleranciaAlterada = tolerancia.trim() !== toleranciaGravada;
  const alteradas = dados.linhas.filter((l) => rascunhos[l.centroId] && mudou(l, rascunhos[l.centroId]!));
  const pendente = toleranciaAlterada || alteradas.length > 0;

  function atualizar(centroId: string, campo: keyof Rascunho, valor: string) {
    setRascunhos((atual) => ({ ...atual, [centroId]: { ...atual[centroId]!, [campo]: valor } }));
    setSalvoEm(false);
  }

  async function gravar() {
    setErro(null);
    const linhas: SalvarConferenciaCpmDto['linhas'] = [];
    for (const l of alteradas) {
      const r = rascunhos[l.centroId]!;
      const textoRef = r.referencia.trim();
      const referencia = textoRef === '' ? null : decimalDigitado(textoRef);
      if (textoRef !== '' && referencia === null) {
        setErro(`CPM de referência inválido em ${l.nome} (ex.: 12,50)`);
        return;
      }
      const explicacao = r.explicacao.trim();
      linhas.push({ centroId: l.centroId, referencia, explicacao: explicacao === '' ? null : explicacao });
    }
    const dto: SalvarConferenciaCpmDto = { linhas };
    if (toleranciaAlterada) {
      const fracao = percentualParaTaxa(tolerancia);
      if (fracao === null) {
        setErro('Tolerância inválida (ex.: 1 para 1%)');
        return;
      }
      dto.tolerancia = fracao;
    }
    try {
      await salvar.mutateAsync({ competencia, dto });
      setSalvoEm(true);
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  return (
    <section aria-label="Conferência do CPM" className="flex flex-col gap-4">
      {dados.origemCpm === 'CALCULADO' && (
        <p role="status" className="rounded-xl border border-warning-ink/30 bg-warning-bg px-4 py-3 text-small text-warning-ink">
          Prévia: o rateio da competência ainda não foi calculado. Os CPMs abaixo são do motor e podem mudar ao calcular.
        </p>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="conferencia-tolerancia" className="text-small font-medium text-ink-secondary">Tolerância (%)</label>
            <input id="conferencia-tolerancia" inputMode="decimal" disabled={!podeEditar} value={tolerancia} onChange={(e) => setTolerancia(e.target.value)} className={`${CAMPO} w-[120px] tabular`} />
          </div>
          <span className="text-small text-ink-secondary" data-testid="resumo-conferencia">
            {dados.resumo.ok} dentro · {dados.resumo.fora} fora · {dados.resumo.semReferencia} sem referência
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BotaoExportar rotulo="Exportar XLSX" exportando={exportacao.exportando} onClick={() => void exportacao.exportar(`/rateios/${competencia}/conferencia/exportar.xlsx`)} />
          <BotaoExportar rotulo="Exportar PDF" exportando={exportacao.exportando} onClick={() => void exportacao.exportar(`/rateios/${competencia}/conferencia/exportar.pdf`)} />
          {podeEditar && (
            <button type="button" disabled={!pendente || salvar.isPending} onClick={() => void gravar()} className="h-[38px] rounded-[10px] bg-primary px-4 font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {salvar.isPending ? 'Salvando…' : 'Salvar'}
            </button>
          )}
        </div>
      </div>

      <ErroExportacao erro={exportacao.erro} />
      {erro && (
        <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          Não foi possível salvar: {erro}
        </p>
      )}
      {salvoEm && !pendente && <p role="status" className="text-caption text-ink-secondary">Conferência gravada.</p>}
      {!podeEditar && <p className="text-caption text-ink-muted">Somente leitura: sem permissão para editar a conferência.</p>}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] border-collapse text-small" aria-label="Conferência do CPM por centro">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-2 py-2.5 font-medium">Centro</th>
              <th scope="col" className="px-2 py-2.5 text-right font-medium">CPM XARP (R$/min)</th>
              <th scope="col" className="px-2 py-2.5 font-medium">CPM de referência (R$/min)</th>
              <th scope="col" className="px-2 py-2.5 text-right font-medium">Diferença R$</th>
              <th scope="col" className="px-2 py-2.5 text-right font-medium">Diferença %</th>
              <th scope="col" className="px-2 py-2.5 font-medium">Situação</th>
              <th scope="col" className="px-2 py-2.5 font-medium">Explicação</th>
              <th scope="col" className="px-2 py-2.5 font-medium">Registrado por</th>
            </tr>
          </thead>
          <tbody>
            {dados.linhas.map((l) => {
              const r = rascunhos[l.centroId] ?? rascunhoDe(l);
              const situacao = SITUACAO[l.situacao];
              return (
                <tr key={l.centroId} data-testid="linha-conferencia" className="border-t border-line-soft align-top">
                  <td className="px-2 py-2.5">
                    <span className="font-mono text-caption text-ink-muted">{l.codigo}</span> <span className="font-semibold">{l.nome}</span>
                  </td>
                  <td className="tabular px-2 py-2.5 text-right">{formatarCusto(l.cpmXarp)}</td>
                  <td className="px-2 py-2.5">
                    <input aria-label={`CPM de referência de ${l.nome}`} inputMode="decimal" disabled={!podeEditar} value={r.referencia} onChange={(e) => atualizar(l.centroId, 'referencia', e.target.value)} className={`${CAMPO} w-[130px] tabular`} />
                  </td>
                  <td className="tabular px-2 py-2.5 text-right">{formatarCusto(l.diferenca)}</td>
                  <td className="tabular px-2 py-2.5 text-right">{formatarFracaoPercentual(l.diferencaPct)}</td>
                  <td className="px-2 py-2.5">
                    <span data-testid="situacao-conferencia" className={`whitespace-nowrap rounded-full px-2 py-0.5 text-caption font-semibold ${situacao.classe}`}>
                      {situacao.rotulo}
                    </span>
                  </td>
                  <td className="px-2 py-2.5">
                    <input aria-label={`Explicação de ${l.nome}`} maxLength={500} disabled={!podeEditar} value={r.explicacao} onChange={(e) => atualizar(l.centroId, 'explicacao', e.target.value)} className={`${CAMPO} w-[220px]`} />
                  </td>
                  <td className="px-2 py-2.5 text-caption text-ink-secondary">
                    {l.registradoPor ? `${l.registradoPor.nome}${l.registradoEm ? ` · ${formatarDataHora(l.registradoEm)}` : ''}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
